import { SelectEditorDescription_Type } from "@smartsuite/automation-engine-api";
import type {
  SelectEditorOption,
  AuthenticationMethodDescription,
  Credential,
  IntegrationDescription,
  ActionConfig,
  AutomationsGetCredentialResponse,
} from "@smartsuite/automation-engine-api";
import { t } from "i18next";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import * as R from "remeda";
import { v4 as uuid } from "uuid";
import { CaptionBlock } from "../../../../components/CaptionBlock";
import type { AutomationPlatformOption } from "../../../../utils/constants";
import * as S from "./styled";
import { getIntegrationLogo } from "../../../../utils/integration-logo";
import { useRootStore } from "../../../../stores/hooks";
import { getLocalCssColorVariables } from "../../../../utils/cssVariablesUtils";
import {
  INPUT_ACTION_NAME_ID,
  Integrations,
} from "../../../../utils/constants";
import SvgLogoGoogle from "../../../../../generated/images/LogoGoogle";
import { useEngineClient } from "../../../../services/engineClient";
import { translateIfPresent } from "../../../../locales/i18n";
import {
  Icon,
  KeyValueCustomSelect,
  MenuLineSeparator,
  MenuOption,
  MenuOptionWrapper,
  Paragraph,
  SubmenuMenuOption,
  Tooltip,
  TooltipButton,
  useSelectState,
  Text,
} from "@smartsuite/react-ui/lib";
import {
  GrayColors,
  SolutionColors,
} from "@smartsuite/types/lib/constants/color";
import { TextColorNames } from "@smartsuite/types";
import { DisplayState, UsedAutomations } from "./used-automations";
import {
  findInputAndPick,
  pickValueConfigString,
} from "../../../../utils/inputs";
import { getSVG } from "../../../../utils/get-svg";
import ImageIcon from "../../Forms/InputForm/SelectEditor/SupportedFeatures/ImageIcon";
import PdfIcon from "../../Forms/InputForm/SelectEditor/SupportedFeatures/PdfIcon";
import { isCredentialIdEmpty } from "./no-creds";

const HELP_LINK =
  "https://help.smartsuite.com/en/articles/11729585-ai-platforms-and-models";

export const AICredentialSelector: React.FC<{
  action: ActionConfig;
  integrationId?: string;
  integrations: IntegrationDescription[];
  credentialId?: string;
  credentials: Credential[];
  onChange: (id: string) => void;
  onAdd: (
    authMethod: AuthenticationMethodDescription,
    initialValues: { [key: string]: string },
  ) => void;
  onRemove: (id: string) => void;
  isSubmitted: boolean;
  loading: boolean;
  isAction?: boolean;
}> = ({
  action,
  integrationId,
  integrations,
  credentialId,
  credentials,
  onChange,
  onAdd,
  onRemove,
  isSubmitted,
  loading,
  isAction,
}) => {
  const store = useRootStore();
  const [options, setOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [authMethods, setAuthMethods] = useState<
    Array<{
      option: AutomationPlatformOption;
      value: AuthenticationMethodDescription;
    }>
  >([]);
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [integration, setIntegration] = useState<IntegrationDescription>();
  const [usedAutomationsModalState, setUsedAutomationsModalState] =
    useState<DisplayState>(DisplayState.CLOSED);
  const [selectState, setSelectState] = useSelectState();
  const [providers, setProviders] = useState<SelectEditorOption[]>();

  const dropdownPanelRef = useRef<HTMLDivElement>(null);

  const engineClient = useEngineClient();

  const label = useMemo(() => {
    if (integration?.authenticationMethods[0]?.label !== undefined) {
      return integration.authenticationMethods[0].label;
    }
    return `${integration?.label ?? ""} ${t("automation-form.account")}`;
  }, [integration]);

  useEffect(() => {
    const integration = integrations.find(
      (integration) => integration.integrationId === integrationId,
    );
    if (integration !== undefined) {
      setIntegration(integration);
    }
  }, [integrationId, integrations]);

  useEffect(() => {
    if (
      integration !== undefined &&
      integration.authenticationMethods.length > 0 &&
      integration.authenticationMethods[0].type.oneofKind === "dynamicInputs" &&
      integration.authenticationMethods[0].type.dynamicInputs.inputs[0].editor
        ?.editor.oneofKind === "select" &&
      integration.authenticationMethods[0].type.dynamicInputs.inputs[0].editor
        ?.editor.select.type ===
        SelectEditorDescription_Type.DROPDOWN_WITH_DETAILS
    ) {
      setProviders(
        integration.authenticationMethods[0].type.dynamicInputs.inputs[0].editor
          ?.editor.select.options,
      );
    }
  }, [integration]);

  useEffect(() => {
    store.modal.modal.closeOnClickOutside =
      usedAutomationsModalState === DisplayState.CLOSED;
  }, [usedAutomationsModalState]);

  useEffect(() => {
    (async () => {
      if (integration === undefined) {
        return;
      }
      const credentialList = credentials.filter(
        (c) =>
          c.authenticationMethodReference?.integrationId === integrationId &&
          (integration !== undefined &&
          integration?.authenticationMethods.length > 0
            ? integration?.authenticationMethods[0].authenticationMethodId ===
              c.authenticationMethodReference?.authenticationMethodId
            : true),
      );

      setOptions(credentialList.map(createCredentialOption));

      if (!isCredentialIdEmpty(credentialId)) {
        const selectedCred = credentialList.find(
          (cred) => cred.credentialId === credentialId,
        );

        if (selectedCred === undefined) {
          // Try to get credential by id
          if (integrationId !== undefined) {
            let cred: AutomationsGetCredentialResponse | undefined;
            if (credentialId === undefined) {
              return;
            }
            try {
              cred = await engineClient.getCredentialById({
                integrationId,
                credentialId,
              });
            } catch (e) {
              setSelectedOption("");
            }
            if (cred?.credential) {
              credentialList.push(cred.credential);
              setSelectedOption(cred.credential.credentialId);
              setOptions(credentialList.map(createCredentialOption));
            }
          }
        } else {
          setSelectedOption(createCredentialOption(selectedCred).value);
        }
      }

      setAuthMethods(
        (integration.authenticationMethods ?? []).map((authMethod) => ({
          option: createAuthMethodOption(integration?.label),
          value: authMethod,
        })),
      );
    })().catch((err) => {
      console.error(err);
    });
  }, [integrationId, integration, credentialId, credentials]);

  const removeCredential = (credentialId: string): void => {
    const usedConnections: Array<{ link: string; automationId: string }> = [];
    store.automations.list.forEach((automation) => {
      if (
        automation.automationId ===
        store.automations.candidate.self.automationId
      ) {
        return;
      }
      const actions = store.automations.candidate
        .automationAllActions(automation)
        .filter((action) => action.credentialId === credentialId);
      actions.forEach((action) => {
        let actionName =
          findInputAndPick(
            action.inputs,
            INPUT_ACTION_NAME_ID,
            pickValueConfigString,
          ) ?? "";
        if (actionName === "") {
          const integration = store.integrations.list.find(
            (integration) =>
              integration.integrationId ===
              action.actionReference?.integrationId,
          );
          if (integration !== undefined) {
            const integrationAction = integration.actions.find(
              (item) => item.actionId === action.actionReference?.actionId,
            );
            if (integrationAction !== undefined) {
              actionName = integrationAction.label;
            }
          }
        }
        usedConnections.push({
          link: `${automation.label} • ${actionName}`,
          automationId: automation.automationId,
        });
      });
    });

    const description =
      usedConnections.length === 0
        ? t("confirm-modal.no-other-connections")
        : `${t("confirm-modal.delete-connection-all")}</p>`;

    store.modal.openConfirmModal({
      type: "warning",
      title: t("confirm-modal.are-you-sure"),
      description,
      closeOnClickOutside: usedAutomationsModalState === DisplayState.CLOSED,
      body: (
        <S.Center>
          <UsedAutomations
            usedConnections={usedConnections}
            onChangeState={setUsedAutomationsModalState}
          ></UsedAutomations>
        </S.Center>
      ),
      positiveButtonText: t("confirm-modal.yes-delete-connection"),
      negativeButtonText: t("confirm-modal.no-cancel"),
      onClose: () => {
        store.modal.closeConfirmModal();
      },
      onConfirm: () => {
        onRemove(credentialId);
        store.modal.closeConfirmModal();
      },
    });
  };

  // const getProviderLogo = (credentialId: string): JSX.Element => {
  //   const credential = store.credentials.list.find(cred => cred.credentialId === credentialId);
  //   if (credentialId === undefined) {
  //     const IntegrationLogo = integrationId
  //       ? getIntegrationLogo(integrationId)
  //       : false;
  //     return IntegrationLogo ? <IntegrationLogo style={{ marginRight: 10 }} /> : <></>;
  //   } else {
  //     providers?.find(provider => {
  //       provider.label === credential?.label
  //     });
  //   }
  //   // console.log("ICON:", option, JSON.parse(JSON.stringify(credential)), store.credentials);
  // }

  const renderOption = (
    option: { value: string; label: string },
    active: boolean,
  ): React.ReactNode => {
    const isAddAuthOption = R.isTruthy(
      authMethods.find((m) => m.option.value === option.value),
    );
    const IntegrationLogo = integrationId
      ? getIntegrationLogo(integrationId)
      : false;

    const credential = credentials.find(
      (credential) =>
        credential.credentialId === option.value &&
        credential.metadata?.metadata.sid !== undefined,
    );

    return isAddAuthOption ? (
      <S.AddNewItem>
        <S.PlusIconWrap>
          <Icon name={"plus"} color={TextColorNames.primary} />
        </S.PlusIconWrap>
        {option.label}
      </S.AddNewItem>
    ) : (
      <S.SelectItem className={active ? "active" : ""}>
        {credential === undefined
          ? IntegrationLogo && <IntegrationLogo style={{ marginRight: 10 }} />
          : renderIcon(`logo-${String(credential.metadata?.metadata.sid)}`)}
        {option.label}
        <S.IconsWrap>
          {active && (
            <Icon
              size={"M"}
              name={"checkmark"}
              color={SolutionColors.basePrimaryGreen}
              className={"checkmark"}
            />
          )}
          {
            <Tooltip
              className={"trash-tooltip"}
              text={t("automation-form.remove-account")}
              placements={["bottom"]}
              target={
                <Icon
                  size={"S"}
                  name={"trash-duotone"}
                  color={SolutionColors.basePrimaryGrey}
                  onClick={() => {
                    removeCredential(option.value);
                  }}
                />
              }
            />
          }
        </S.IconsWrap>
      </S.SelectItem>
    );
  };

  const renderHeader: JSX.Element = (
    <S.HeaderWrap>{t("caption.Existing Connections")}</S.HeaderWrap>
  );

  const renderIcon = (iconName?: string): React.ReactElement => {
    const Icon = iconName === undefined ? undefined : getSVG(iconName);
    return Icon ? (
      <S.IconWrap>
        <Icon />
      </S.IconWrap>
    ) : (
      <span />
    );
  };

  const renderInnerIcon = (iconName?: string): React.ReactElement => {
    const Icon = iconName === undefined ? undefined : getSVG(iconName);
    return Icon ? (
      <S.InnerIconWrap>
        <Icon />
      </S.InnerIconWrap>
    ) : (
      <span />
    );
  };

  const renderFooter = (): React.ReactElement => {
    return (
      <S.FooterWrap>
        <S.HeaderWrap>
          {t("caption.Connect New AI Model using API key")}
        </S.HeaderWrap>
        <div ref={dropdownPanelRef}>
          {providers?.map((provider) => {
            const data: {
              slug: string;
              subItems: Array<{
                slug: string;
                label: string;
                description: string;
                price: string;
                speed: number;
                support: any[];
              }>;
            } = JSON.parse(provider.value);
            if (data.subItems === undefined || data.subItems.length === 0) {
              return (
                <MenuOption
                  className="menu-option"
                  key={`provider-${data.slug}`}
                  onClick={() => {
                    onAdd(authMethods[0].value, { sid: data.slug });
                  }}
                >
                  <S.MenuItemWrap>
                    {renderIcon(provider.icon)}
                    {provider.label}
                  </S.MenuItemWrap>
                </MenuOption>
              );
            } else {
              return (
                <SubmenuMenuOption
                  key={`provider-${data.slug}`}
                  name={provider.label}
                  parentMenu={dropdownPanelRef}
                  renderIcon={() => renderIcon(provider.icon)}
                  submenuArrowIcon="arrow-right-thin"
                  submenuArrowIconSize="S"
                  autohide
                  delayShow
                >
                  <S.SubMenu>
                    <MenuOptionWrapper className="sub-menu-models">
                      {data.subItems.map((item) => {
                        return (
                          <S.Option
                            key={`provider-${data.slug}-${item.slug}`}
                            onClick={() => {
                              store.automations.candidate.setActionInput(
                                action,
                                "model",
                                {
                                  values: [
                                    {
                                      value: {
                                        oneofKind: "string",
                                        string: item.slug,
                                      },
                                    },
                                  ],
                                },
                              );
                              onAdd(authMethods[0].value, { sid: data.slug });
                            }}
                          >
                            <S.ContentWrapper>
                              <S.IconWrapper>
                                {renderInnerIcon(provider.icon)}
                              </S.IconWrapper>
                              <S.ContentColumn>
                                <Text
                                  wrapMode="ellipsis"
                                  weight="medium"
                                  color="currentColor"
                                >
                                  {item.label} &nbsp;
                                  {item.support !== undefined &&
                                    (item.support as string[]).includes(
                                      "Image",
                                    ) && (
                                      <Tooltip
                                        placements={["top"]}
                                        maxWidth={140}
                                        target={<ImageIcon />}
                                        text={t(
                                          "caption.This model can scan Images Files",
                                        )}
                                      />
                                    )}
                                  {item.support !== undefined &&
                                    (item.support as string[]).includes(
                                      "PDF",
                                    ) && (
                                      <Tooltip
                                        placements={["top"]}
                                        maxWidth={140}
                                        target={<PdfIcon />}
                                        text={t(
                                          "caption.This model can read PDF Files",
                                        )}
                                      />
                                    )}
                                </Text>
                                <Text
                                  wrapMode="ellipsis"
                                  weight="regular"
                                  color="inactive"
                                  size="xs"
                                >
                                  {item.price} &nbsp; {item.description}
                                </Text>
                              </S.ContentColumn>
                            </S.ContentWrapper>
                          </S.Option>
                        );
                      })}
                      <MenuLineSeparator />
                      <S.SubmenuFooter>
                        <TooltipButton
                          iconName="info-filled"
                          iconSize="S"
                          colors={{
                            default: GrayColors["G-3"],
                            hover: GrayColors["G-8"],
                          }}
                          handleClick={() => {
                            window.open(HELP_LINK, "_blank");
                          }}
                        />
                        &nbsp;
                        <a href={HELP_LINK} target="_blank" rel="noopener">
                          {t("learn-more2")}{" "}
                        </a>
                        &nbsp;
                        {t("caption.about-model", {
                          label: provider.label,
                        })}
                      </S.SubmenuFooter>
                    </MenuOptionWrapper>
                  </S.SubMenu>
                </SubmenuMenuOption>
              );
            }
          })}
        </div>
      </S.FooterWrap>
    );
  };

  const Logo = getIntegrationLogo(integrationId ?? "");

  const renderSelected = (option: {
    value: string;
    label: string;
  }): React.ReactNode => {
    return (
      <S.SelectedItem>
        {integrationId?.startsWith(Integrations.google) ? (
          <SvgLogoGoogle />
        ) : (
          <Logo />
        )}
        {option.label}
      </S.SelectedItem>
    );
  };

  const hasCredentialError =
    (isSubmitted && isCredentialIdEmpty(selectedOption)) ||
    store.automations.candidate.triggerPermissionsError !== undefined;
  const credentialSelectState = hasCredentialError ? "error" : selectState;

  return (
    <S.SelectFieldContainer $hasError={hasCredentialError}>
      <CaptionBlock
        description={{
          icon: "",
          label,
          subLabel: integration?.authenticationMethods[0].helpText ?? "",
          display: {
            showRequiredStatus: 1,
            display: { oneofKind: "always", always: 0 },
          },
          dynamic: !loading,
        }}
      />

      <KeyValueCustomSelect
        className={hasCredentialError ? "is-error" : ""}
        value={selectedOption}
        placeholder={
          credentialSelectState === "active"
            ? t("automation-form.credential-selector-new")
            : "\u00a0"
        }
        placeholderArrowStyle="filled-dark"
        portalStyles={getLocalCssColorVariables(store.theme)}
        showClear={false}
        options={options}
        state={credentialSelectState}
        renderSelectedOption={renderSelected}
        renderOption={renderOption}
        renderDropdownHeader={renderHeader}
        renderDropdownFooter={renderFooter}
        onOpenedChange={setSelectState}
        onChange={(value) => {
          if (value !== credentialId) {
            const option = options.find((option) => option.value === value);
            if (option !== undefined) {
              setSelectedOption(String(value));
              onChange(String(value));
            }
          }
        }}
      />
      {!isAction &&
        store.automations.candidate.triggerPermissionsError !== undefined && (
          <Paragraph color="error" size="xs">
            {translateIfPresent(
              `caption.${store.automations.candidate.triggerPermissionsError}`,
              store.automations.candidate.triggerPermissionsError,
            )}
          </Paragraph>
        )}

      {isSubmitted && isCredentialIdEmpty(selectedOption) ? (
        <Paragraph color="error" size="xs">
          {t("automation-form.this-field-is-required")}
        </Paragraph>
      ) : null}
    </S.SelectFieldContainer>
  );
};

const createCredentialOption = (
  credential: Credential,
): AutomationPlatformOption => ({
  value: credential.credentialId,
  label: credential.label,
});

const createAuthMethodOption = (
  type: string = "",
): AutomationPlatformOption => ({
  value: uuid(),
  label: t("automation-form.credential-selector-add-option", { type }),
});
